import React, { useEffect, useState, useRef,  useContext, useImperativeHandle } from 'react';
import { TextField, ITextField, Stack, Icon } from '@fluentui/react';
import { createPortal } from 'react-dom';
import { getBlobList } from '../../api';
import { BlobListItem } from '../../api/models';
import { AppStateContext } from "../../state/AppProvider";
import './KnowledgeDropdown.css';

interface KnowledgeDropdownProps {
  selectedDocument: string;
  onDocumentChange: (documentName: string, documentLink: string) => void;
  placeholder?: string;
  clearButtonLabel?: string;
  onEscapeKeyPressed?: () => void; // Callback when escape is pressed to close modal
  modalContainerSelector?: string; // CSS selector for the modal container
  pgm: string; // Program code for API calls  
}

// Expose methods for parent components to call
export interface KnowledgeDropdownRef {
  focusInput: () => void;
}

const KnowledgeDropdown = React.forwardRef<KnowledgeDropdownRef, KnowledgeDropdownProps>(({
  selectedDocument,
  onDocumentChange,
  placeholder = "Select Document",
  clearButtonLabel = "Clear selection",
  onEscapeKeyPressed,
  modalContainerSelector,
  pgm,
}, ref) => {
  const [knowledgeItems, setKnowledgeItems] = useState<BlobListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const searchInputRef = useRef<ITextField>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const appStateContext = useContext(AppStateContext)
  var settings = appStateContext?.state.frontendSettings;
  const { msalInstance, b2cAuthResponse } = appStateContext?.state || {};
  // Helper function to focus the input field
  const focusInput = () => {
    // Try immediate focus first
    const inputElement = containerRef.current?.querySelector('input') as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
      return;
    }

    // Fallback with delay if immediate focus fails
    setTimeout(() => {
      if (searchInputRef.current) {
        try {
          searchInputRef.current.focus();
        } catch (e) {
          console.log('Fluent UI focus failed, trying DOM approach');
        }
      }
      
      const inputElementDelayed = containerRef.current?.querySelector('input') as HTMLInputElement;
      if (inputElementDelayed) {
        inputElementDelayed.focus();
      }
    }, 50);
  };

  // Expose focusInput method to parent components
  useImperativeHandle(ref, () => ({
    focusInput
  }));

  // Handle escape key
  useEffect(() => {
    // Global escape key handler that overrides parent handlers
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Always prevent default and stop propagation to override parent handlers
        event.preventDefault();
        event.stopPropagation();
        
        if (isDropdownOpen) {
          // If dropdown is open, just close it
          setIsDropdownOpen(false);
          setSearchText("");
          setFocusedIndex(-1);
          focusInput();
        } else {
          // If dropdown is closed, close the modal
          if (onEscapeKeyPressed) {
            onEscapeKeyPressed();
          }
        }
      }
    };

    // Always listen for escape key when component is mounted (modal is open)
    document.addEventListener('keydown', handleEscapeKey, true); // Use capture phase to intercept before parent handlers

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isDropdownOpen, onEscapeKeyPressed]);

  // Reset focused index when dropdown closes or search text changes
  useEffect(() => {
    if (!isDropdownOpen) {
      setFocusedIndex(-1);
    } else {
      // When dropdown opens, ensure focus is on the input - try immediately and with delay
      const inputElement = containerRef.current?.querySelector('input') as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
        // Also try with a small delay in case the above doesn't work
        setTimeout(() => inputElement.focus(), 10);
        setTimeout(() => inputElement.focus(), 50);
      }
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [searchText]);

  // Fetch blob list on component mount
  useEffect(() => {
    const fetchKnowledgeBase = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const items = await getBlobList(false, pgm, msalInstance, settings);
        if (items && items.length > 0) {
          setKnowledgeItems(items);
        } else if (items && items.length === 0) {
          // Empty array means no documents found
          setError('No documents were found. Please try again later.');
          setKnowledgeItems([]);
        } else {
          // Null/undefined means API error
          setError('Knowledge base temporarily unavailable. Please try again later');
        }
      } catch (err) {
        console.error('Error fetching knowledge base:', err);
        setError('Knowledge base temporarily unavailable. Please try again later');
      } finally {
        setIsLoading(false);
      }
    };

    fetchKnowledgeBase();
  }, []);

  // Get filtered items based on search text or selected document
  const getFilteredItems = (): BlobListItem[] => {
    if (!knowledgeItems || knowledgeItems.length === 0) {
      return [];
    }

    let filteredItems = knowledgeItems;
    
    // Determine what text to filter by
    const currentValue = selectedDocument && !searchText ? selectedDocument : searchText;
    
    // Filter based on current value (either search text or selected document)
    if (currentValue.trim()) {
      const searchLower = currentValue.toLowerCase().trim();
      filteredItems = knowledgeItems.filter(item => 
        item.name.toLowerCase().includes(searchLower)
      );
    }

    return filteredItems;
  };





  // Handle clicks outside to close dropdown or modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const targetElement = target as Element;
      
      // Check if click is inside the main component container (input field and buttons)
      const isInsideContainer = containerRef.current && containerRef.current.contains(target);
      
      // Check if click is inside the dropdown portal
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(target);
      
      // Check if click is inside any knowledge dropdown element using class names (fallback)
      const isInsideComponent = targetElement.closest?.('.knowledge-dropdown-container') || 
                               targetElement.closest?.('.knowledge-dropdown-portal');
      
      // If click is inside any part of our component (input, buttons, or dropdown), don't do anything
      if (isInsideContainer || isInsideDropdown || isInsideComponent) {
        return;
      }
      
      // If dropdown is open, just close the dropdown (don't close modal)
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        setSearchText("");
        setFocusedIndex(-1);
        return; // Don't proceed to modal closing logic
      }
      
      // Check if click is inside the entire knowledge base modal popup
      let shouldCloseModal = true;
      
      // Primary check: Use the most specific and reliable detection method
      // Look for the slide-pane content container first (most specific)
      const slidePaneContent = targetElement.closest?.('.slide-pane__content');
      if (slidePaneContent) {
        shouldCloseModal = false;
      }
      
      // Secondary check: If no slide-pane, check for provided modal container selector
      if (shouldCloseModal && modalContainerSelector) {
        const modalContainer = document.querySelector(modalContainerSelector);
        if (modalContainer && modalContainer.contains(target)) {
          shouldCloseModal = false;
        }
      }
      
      // Tertiary check: Look for profile modal main container (fallback)
      if (shouldCloseModal) {
        const profileModalMain = targetElement.closest?.('[class*="profileModalMain"]');
        if (profileModalMain) {
          shouldCloseModal = false;
        }
      }
      
      // Close modal only if click is determined to be outside the entire modal popup
      if (shouldCloseModal && onEscapeKeyPressed) {
        onEscapeKeyPressed(); // Close the modal and return focus to KB icon
      }
    };

    // Always listen for clicks outside when component is mounted
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, modalContainerSelector, onEscapeKeyPressed]);

  // Position dropdown relative to fieldGroup with viewport positioning
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number, width: number} | null>(null);
  
  const updateDropdownPosition = () => {
    if (isDropdownOpen && containerRef.current) {
      const fieldGroup = containerRef.current.querySelector('.ms-TextField-fieldGroup');
      
      if (fieldGroup) {
        const fieldGroupRect = fieldGroup.getBoundingClientRect();
        
        // Calculate position relative to viewport for portal positioning
        const topOffset = fieldGroupRect.bottom; // Position below input field (viewport coordinates)
        const dropdownWidth = fieldGroupRect.width; // Same width as input field
        const leftOffset = fieldGroupRect.left; // Align left edge with input field
        
        setDropdownPosition({
          top: topOffset,
          left: leftOffset,
          width: dropdownWidth
        });
      }
    } else {
      setDropdownPosition(null);
    }
  };
  
  useEffect(() => {
    updateDropdownPosition();
  }, [isDropdownOpen]);
  
  // Update position on scroll/resize
  useEffect(() => {
    if (isDropdownOpen) {
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isDropdownOpen]);

  // Show error state
  if (error) {
    return (
      <div className="knowledge-dropdown-error" style={{ position: 'relative' }}>
        <TextField
          placeholder={error}
          disabled={true}
          styles={{
            root: { 
              borderRadius: '8px',
              margin: '10px auto',
              marginLeft: '10px',
              marginRight: '15px',
              width: 'calc(100% - 25px)', // Match VIEW DOCUMENT button width (10px + 15px margins)
              maxWidth: 'calc(100% - 25px)'
            },
            field: { 
              fontSize: 'var(--YW_Body_InputField_Font_Size)',
              fontFamily: 'var(--YW_Theme_Font_Type)',
              color: 'var(--YW_Body_InputField_Font_Color)',
              backgroundColor: 'transparent' // Let fieldGroup handle the background
            },
            fieldGroup: {
              borderRadius: '8px',
              border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
              backgroundColor: 'var(--YW_Theme_InputField_Background_Color) !important',
              overflow: 'hidden', // Clip content to respect rounded corners
              margin: '1px', // Same margin approach as main component
              selectors: {
                '&[disabled]': {
                  border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
                  backgroundColor: 'var(--YW_Theme_InputField_Background_Color) !important'
                }
              }
            }
          }}
        />
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="knowledge-dropdown-loading" style={{ position: 'relative' }}>
        <TextField
          placeholder="Loading documents..."
          disabled={true}
          styles={{
            root: { 
              border: '1px solid #d1d1d1',
              borderRadius: '8px',
              margin: '10px auto',
              marginLeft: '10px',
              marginRight: '15px',
              width: 'calc(100% - 25px)',
              maxWidth: 'calc(100% - 25px)'
            },
            fieldGroup: {
              borderRadius: '8px',
              border: 'none',
            }
          }}
        />
      </div>
    );
  }

  const filteredItems = getFilteredItems();

  return (
    <div 
      className="knowledge-dropdown-container" 
      ref={containerRef} 
      style={{ position: 'relative' }}
    >
      <Stack className="knowledge-dropdown-stack">
        {/* Search TextField with integrated buttons */}
        <div style={{ position: 'relative' }}>
          <TextField
            componentRef={searchInputRef}
            placeholder={searchText === "" && !selectedDocument ? "Search or select document..." : ""}
            value={searchText !== "" ? searchText : (selectedDocument || "")}
            role="combobox"
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
            aria-autocomplete="list"
           aria-controls={isDropdownOpen && filteredItems.length > 0 ? "knowledge-dropdown-listbox" : undefined}
           aria-activedescendant={isDropdownOpen && focusedIndex >= 0 && focusedIndex < filteredItems.length ? `knowledge-dropdown-option-${focusedIndex}` : undefined}
            onChange={(_, newValue) => {
              setSearchText(newValue || "");
              // If user clears the input completely, also clear the selected document
              if (newValue === "" || newValue === null || newValue === undefined) {
                onDocumentChange("", "");
              }
              // Open dropdown when user starts typing (but not when they clear it)
              if (newValue && newValue.trim() !== "") {
                setIsDropdownOpen(true);
              }
            }}
            styles={{
              root: { 
                borderRadius: '8px',
                margin: '10px auto',
                marginLeft: '10px',
                marginRight: '15px',
                width: 'calc(100% - 25px)', // Match VIEW DOCUMENT button width (10px + 15px margins)
                maxWidth: 'calc(100% - 25px)'
              },
              field: { 
                fontSize: 'var(--YW_Body_InputField_Font_Size)',
                fontFamily: 'var(--YW_Theme_Font_Type)',
                color: 'var(--YW_Body_InputField_Font_Color)',
                backgroundColor: 'transparent' // Let fieldGroup handle the background
              },
              suffix: {
                display: 'flex',
                alignItems: 'center',
                paddingRight: '8px',
                background: 'transparent',
                backgroundColor: 'transparent',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                overflow: 'hidden'
              },
              fieldGroup: {
                borderRadius: '8px',
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
                backgroundColor: 'var(--YW_Theme_InputField_Background_Color)',
                overflow: 'hidden', // Clip content to respect rounded corners
                margin: '1px', // Reserve space for thicker focus border
                selectors: {
                  '&:hover': {
                    border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
                    backgroundColor: 'var(--YW_Theme_InputField_Background_Color)'
                  },
                  '&:focus-within': {
                    outline: 'none !important',
                    border: '2px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
                    margin: '0px !important', // Remove margin when border gets thicker
                    boxShadow: 'none !important',
                    backgroundColor: 'var(--YW_Theme_InputField_Background_Color)'
                  },
                  '&:after': {
                    border: 'none !important'
                  }
                }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!isDropdownOpen) {
                  setIsDropdownOpen(true);
                }
                setFocusedIndex(prev => 
                  prev < filteredItems.length - 1 ? prev + 1 : 0
                );
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedIndex(prev => 
                  prev > 0 ? prev - 1 : filteredItems.length - 1
                );
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredItems.length) {
                  // Select focused item
                  const focusedItem = filteredItems[focusedIndex];
                  onDocumentChange(focusedItem.name, focusedItem.url);
                  setSearchText("");
                  setIsDropdownOpen(false);
                  setFocusedIndex(-1);
                } else if (filteredItems.length > 0) {
                  // Select first filtered item if none focused
                  const firstItem = filteredItems[0];
                  onDocumentChange(firstItem.name, firstItem.url);
                  setSearchText("");
                  setIsDropdownOpen(false);
                  setFocusedIndex(-1);
                }
              } else if (e.key === 'Escape') {
                if (isDropdownOpen) {
                  // Close only the dropdown, not the surrounding modal
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  setFocusedIndex(-1);
                }
              } else if (e.key === 'Tab') {
                // Let focus move on, but close the open list behind it
                if (isDropdownOpen) {
                  setIsDropdownOpen(false);
                  setFocusedIndex(-1);
                }
              }
            }}
            disabled={isLoading || !!error || knowledgeItems.length === 0}
            onRenderSuffix={() => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* Clear button - custom HTML element */}
                {(selectedDocument || searchText) && (
                  <div
                    className="knowledge-dropdown-clear-button"
                    title={clearButtonLabel}
                    aria-label={clearButtonLabel}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onDocumentChange("", "");
                      setSearchText("");
                      setIsDropdownOpen(false);
                      setFocusedIndex(-1);
                      // Return focus to input field after clearing
                      focusInput();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDocumentChange("", "");
                        setSearchText("");
                        setIsDropdownOpen(false);
                        setFocusedIndex(-1);
                        // Return focus to input field after clearing
                        focusInput();
                      }
                    }}
                    style={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      background: 'transparent',
                      padding: 0,
                      color: 'var(--YW_SignIn_Modal_SwitchCase_Dropdown_Caret_Color)',
                      fontSize: '16px'
                    }}
                  >
                    ✕
                  </div>
                )}
                
                {/* Dropdown button - using Fluent UI ChevronDown icon */}
                <div
                  className="knowledge-dropdown-toggle-button"
                  title={isDropdownOpen ? 'Hide options' : 'Show options'}
                  aria-label={isDropdownOpen ? 'Hide options' : 'Show options'}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    // Don't allow click if there are no documents at all
                    if (isLoading || !!error || knowledgeItems.length === 0) {
                      return;
                    }
                    
                    const newDropdownState = !isDropdownOpen;
                    setIsDropdownOpen(newDropdownState);
                    setFocusedIndex(-1);
                    
                    // When opening dropdown, focus the input field immediately using multiple strategies
                    if (newDropdownState) {
                      // Strategy 1: Immediate focus
                      const inputElement = containerRef.current?.querySelector('input') as HTMLInputElement;
                      if (inputElement) {
                        inputElement.focus();
                      }
                      
                      // Strategy 2: Use requestAnimationFrame for next paint cycle
                      requestAnimationFrame(() => {
                        const inputEl = containerRef.current?.querySelector('input') as HTMLInputElement;
                        if (inputEl) {
                          inputEl.focus();
                        }
                      });
                      
                      // Strategy 3: Fallback with setTimeout
                      setTimeout(() => {
                        const inputEl = containerRef.current?.querySelector('input') as HTMLInputElement;
                        if (inputEl) {
                          inputEl.focus();
                        }
                      }, 10);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      
                      // Don't allow interaction if there are no documents at all
                      if (isLoading || !!error || knowledgeItems.length === 0) {
                        return;
                      }
                      
                      const newDropdownState = !isDropdownOpen;
                      setIsDropdownOpen(newDropdownState);
                      setFocusedIndex(-1);
                      
                      // When opening dropdown via keyboard, focus the input field immediately
                      if (newDropdownState) {
                        // Strategy 1: Immediate focus
                        const inputElement = containerRef.current?.querySelector('input') as HTMLInputElement;
                        if (inputElement) {
                          inputElement.focus();
                        }
                        
                        // Strategy 2: Use requestAnimationFrame for next paint cycle
                        requestAnimationFrame(() => {
                          const inputEl = containerRef.current?.querySelector('input') as HTMLInputElement;
                          if (inputEl) {
                            inputEl.focus();
                          }
                        });
                        
                        // Strategy 3: Fallback with setTimeout
                        setTimeout(() => {
                          const inputEl = containerRef.current?.querySelector('input') as HTMLInputElement;
                          if (inputEl) {
                            inputEl.focus();
                          }
                        }, 10);
                      }
                    }
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isLoading || !!error || knowledgeItems.length === 0 ? 'default' : 'pointer',
                    background: 'transparent',
                    padding: 0,
                    opacity: isLoading || !!error || knowledgeItems.length === 0 ? 0.5 : 1
                  }}
                >
                  <Icon
                    iconName={isDropdownOpen ? "ChevronUp" : "ChevronDown"}
                    style={{
                      color: 'var(--YW_SignIn_Modal_SwitchCase_Dropdown_Caret_Color)',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>
              </div>
            )}
          />
        </div>

      </Stack>
      
      {/* Hidden translatable text for Google Translate */}
      <div className="knowledge-dropdown-hidden-labels">
        <span>{placeholder}</span>
      </div>
      
      {/* Portal-based dropdown that renders outside the modal */}
      {isDropdownOpen && dropdownPosition && createPortal(
          <>
            {/* Dropdown options list - show when open and has options */}
            {filteredItems.length > 0 ? (
            <div 
              ref={dropdownRef}
              id="knowledge-dropdown-listbox"
              className="knowledge-dropdown-options knowledge-dropdown-portal"
              role="listbox"
              aria-label="Knowledge base documents"
              onMouseDown={(e) => {
                // Prevent clicks within dropdown from bubbling up to document click handler
                e.stopPropagation();
              }}
              onClick={(e) => {
                // Prevent clicks within dropdown from bubbling up to document click handler
                e.stopPropagation();
              }}
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top + 1}px`, // +1px to overlap with input border
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                maxHeight: '300px',
                minHeight: '40px', // Ensure minimum height for scrolling
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
                borderRadius: '8px', // All corners rounded
                borderTop: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
                backgroundColor: 'var(--YW_Theme_InputField_Background_Color)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 10000, // Very high z-index to appear above modal
                boxSizing: 'border-box',
                fontSize: 'var(--YW_Body_InputField_Font_Size)',
                fontFamily: 'var(--YW_Theme_Font_Type)',
                overflow: 'hidden' // Hide overflow on container
              }}
            >
              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                className="knowledge-dropdown-scroll-container"
              >
                {filteredItems.map((item, index) => {
                  const isSelected = selectedDocument === item.name;
                  const isFocused = focusedIndex === index;
                  const isHighlighted = isSelected || isFocused;
                  
                  return (
                  <div
                    key={item.name}
                    id={`knowledge-dropdown-option-${index}`}
                    className="knowledge-dropdown-option"
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={-1}
                    ref={(el) => {
                      if (isFocused && el) {
                        el.scrollIntoView({ block: 'nearest' });
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 'var(--YW_Body_InputField_Font_Size)',
                      fontFamily: 'var(--YW_Theme_Font_Type)',
                      backgroundColor: isHighlighted ? 'var(--YW_Header_Program_Dropdown_Hover_Color)' : 'var(--YW_Theme_InputField_Background_Color)',
                      color: isHighlighted ? 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)' : 'var(--YW_Body_InputField_Font_Color)',
                      boxSizing: 'border-box',
                      outline: isFocused ? '2px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)' : 'none',
                      outlineOffset: '-2px'
                    }}
                    onMouseEnter={(e) => {
                      setFocusedIndex(index);
                    }}
                    onMouseLeave={(e) => {
                      // Don't reset focused index on mouse leave to maintain keyboard focus
                    }}
                    onClick={() => {
                      onDocumentChange(item.name, item.url);
                      setSearchText("");
                      setIsDropdownOpen(false);
                      setFocusedIndex(-1);
                    }}
                  >
                    {item.name}
                  </div>
                  );
                })}
              </div>
            </div>
            ) : knowledgeItems.length > 0 && (searchText.trim() !== "" || selectedDocument !== "") ? (
            /* No matches found - show when there are documents available but search has no results */
            <div 
              ref={dropdownRef}
              className="knowledge-dropdown-options knowledge-dropdown-portal"
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top + 1}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                minHeight: '40px',
                border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
                borderRadius: '8px',
                backgroundColor: 'var(--YW_Theme_InputField_Background_Color)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 10000,
                boxSizing: 'border-box',
                fontSize: 'var(--YW_Body_InputField_Font_Size)',
                fontFamily: 'var(--YW_Theme_Font_Type)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px'
              }}
            >
              <span style={{ 
                color: 'var(--YW_Body_InputField_Font_Color)',
                opacity: 0.7,
                fontStyle: 'italic'
              }}>
                No matches found
              </span>
            </div>
            ) : null}
          

        </>,
        document.body
      )}
    </div>
  );
});

KnowledgeDropdown.displayName = 'KnowledgeDropdown';

export default KnowledgeDropdown;
